import React from "react";
import { Button } from "@mui/material";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Download } from "@mui/icons-material";
// import handleExport from "../../components/exportExcellFile";

const ExportEmployeeExcel = ({color}) => {

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    
        const employeeSheet = workbook.addWorksheet("1. Employee Database");
    
        // --- TITLE ---
        employeeSheet.addRow(["Employee Database"]);
        employeeSheet.getCell("A1").font = { bold: true };
        employeeSheet.getCell("A1").alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    
        // --- INSTRUCTION ---
        employeeSheet.addRow(["Please fill up the following information."]);
        employeeSheet.getCell("A2").font = { italic: true, color: { argb: "FF6E6E6E" } }; // grey italic text
        employeeSheet.getCell("A2").alignment = { vertical: "top", horizontal: "left", wrapText: false };
    
        // --- SPACING ROW ---
        employeeSheet.addRow([]);
    
        // --- COLUMN WIDTH FOR "No." ---
        employeeSheet.getColumn(1).width = 15; // Fixed width for "No."
    
        // --- HEADER ROW ---
        const headerRow = employeeSheet.addRow([]); // create empty row first
    
        const headers = [
          { title: "No.", example: "1, 2, 3..." },
          { title: "Employee Code", example: "ABC001" },
          { title: "Full Name", example: "Ben Tan" },
          { title: "Role", example: "Mission Creator, User" },
          { title: "Email", example: "ben@abc.com" },
          { title: "Gender", example: "Male" },
          { title: "DOB", example: "28.08.1988" },
          { title: "Designation", example: "Business Director" },
          { title: "Department", example: "Human Resource" },
          { title: "Job Grade", example: "G5" },
        ];
    
        // --- APPLY HEADER STYLE ---
        headers.forEach((h, i) => {
          const cell = headerRow.getCell(i + 1);
          cell.value = {
          richText: [
            { text: `${h.title}\n`, font: { bold: true, color: { argb: "FFFFFFFF" } } },
            { text: `(e.g. ${h.example})`, font: { italic: true, color: { argb: "FFDDDDDD" } } },
          ],
          };
          cell.alignment = { vertical: "top", horizontal: "left", wrapText: true };
          cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF39493F" },
          };
          cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
          };
        });
    
        // --- ROLE DROPDOWN (data validation for the Role column) ---
        // Role column index (4th column)
        const roleColIndex = 4;
        const roleOptions = ["User", "Mission Creator", "POISUM Admin"];
        const roleFormula = `"${roleOptions.join(",")}"`;
    
        // Apply data validation for a reasonable number of rows below the header
        const firstDataRow = headerRow.number + 1;
        const lastDataRow = firstDataRow + 999; // e.g. 1000 rows of validation
        for (let r = firstDataRow; r <= lastDataRow; r++) {
          const cell = employeeSheet.getCell(r, roleColIndex);
          cell.dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [roleFormula],
          showInputMessage: true,
          promptTitle: "Select Role",
          prompt: `Choose one of: ${roleOptions.join(", ")}`,
          showErrorMessage: true,
          errorTitle: "Invalid Role",
          error: "Please select a role from the list.",
          };
          // keep alignment consistent
          cell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
        }
    
        // --- AUTO WIDTH FOR OTHER COLUMNS (EXCEPT COLUMN 1) ---
        employeeSheet.columns.forEach((col, idx) => {
          if (idx === 0) return; // Skip column "No."
    
          let maxLength = 0;
          col.eachCell({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value
            ? (typeof cell.value === "object" && cell.value.richText
            ? cell.value.richText.map((t) => t.text).join("\n") // preserve the newline between title and example
            : cell.value.toString())
            : "";
          maxLength = Math.max(maxLength, cellValue.length);
    
          // Enable wrap text so the "(e.g. ...)" appears on a new line
          cell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
          });
    
          // Reasonable width with caps
          col.width = Math.min(Math.max(maxLength + 5, 15), 60);
        });
    
        // --- Job Grade Sheet ---
        const jobGradeSheet = workbook.addWorksheet("2. Job Grade");
    
        // --- TITLE & DESCRIPTION ---
        jobGradeSheet.addRow(["Job Grade"]);
        jobGradeSheet.getCell("A1").font = { bold: true };
        jobGradeSheet.getCell("A1").alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    
        jobGradeSheet.addRow([
          "A classification of employee level or position within the company’s hierarchy, used in POISUM to group roles by seniority and responsibility.",
        ]);
        jobGradeSheet.getCell("A2").alignment = { vertical: "top", horizontal: "left", wrapText: false };
    
        jobGradeSheet.addRow([]);
    
        // --- INSTRUCTION ---
        jobGradeSheet.addRow(["Instruction:"]);
        jobGradeSheet.getCell("A4").font = { bold: true };
        jobGradeSheet.getCell("A4").alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    
        jobGradeSheet.addRow(["Please list down all the job grade in your company."]);
        jobGradeSheet.getCell("A5").alignment = { vertical: "top", horizontal: "left", wrapText: false };
    
        jobGradeSheet.addRow([]);
    
        // --- MAIN TABLE HEADER (LEFT SIDE) ---
        const mainHeaderRow = jobGradeSheet.addRow(["No.", "Job Grade", "Description"]);
        mainHeaderRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF39493F" },
          };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
    
        // --- EXAMPLE TITLE ---
        jobGradeSheet.getCell("E6").value = "Example:";
        jobGradeSheet.getCell("E6").font = { italic: true, color: { argb: "FF6E6E6E" } };
        jobGradeSheet.getCell("E6").alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    
        // --- EXAMPLE TABLE HEADER (RIGHT SIDE) ---
        const exampleHeaderRow = jobGradeSheet.getRow(7);
        ["No.", "Job Grade", "Description"].forEach((title, i) => {
          const cell = exampleHeaderRow.getCell(5 + i);
          cell.value = title;
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF39493F" },
          };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
    
        // --- EXAMPLE DATA ROWS ---
        const exampleData = [
          ["1", "G1", "Intern / Part Timer"],
          ["2", "G2", "Executive / Engineer"],
          ["3", "G3", "Senior Executive / Engineer"],
          ["4", "G4", "Asst Manager"],
          ["5", "G5", "Manager / Specialist"],
          ["6", "G6", "Director / C-levels"],
        ];
    
        exampleData.forEach((dataRow, i) => {
          const row = jobGradeSheet.getRow(8 + i);
          row.getCell(5).value = dataRow[0];
          row.getCell(6).value = dataRow[1];
          row.getCell(7).value = dataRow[2];
          [5, 6, 7].forEach((col) => {
            const cell = row.getCell(col);
            cell.alignment = { vertical: "top", horizontal: "left", wrapText: false };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });
    
        // --- COLUMN WIDTHS ---
        // Left table
        jobGradeSheet.getColumn(1).width = 5;  // No.
        jobGradeSheet.getColumn(2).width = 15; // Job Grade
        jobGradeSheet.getColumn(3).width = 30; // Description
    
        // Space between tables (D)
        jobGradeSheet.getColumn(4).width = 15;
    
        // Right table
        jobGradeSheet.getColumn(5).width = 5;
        jobGradeSheet.getColumn(6).width = 15;
        jobGradeSheet.getColumn(7).width = 30;
    
    
    
        // --- Role Sheet ---
        const roleSheet = workbook.addWorksheet("3. Role");
    
        // ===== TITLE =====
        roleSheet.addRow(["Glossary : Role"]);
        const titleCell = roleSheet.getCell("A1");
        titleCell.font = { bold: true, size: 12 };
        titleCell.alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    
        // ===== DESCRIPTION =====
        const descRow = roleSheet.addRow([
          "Everyone plays a part — some create missions, some run the portal, and everyone joins in to earn rewards.",
        ]);
        const descRowIndex = descRow.number;
        roleSheet.mergeCells(descRowIndex, 1, descRowIndex, 4);
        const descCell = roleSheet.getCell(descRowIndex, 1);
        descCell.font = { size: 11 };
        descCell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
    
        roleSheet.addRow([]);
    
        // ===== INSTRUCTION =====
        const instructionTitleRow = roleSheet.addRow(["Instruction:"]);
        instructionTitleRow.getCell(1).font = { bold: true };
        instructionTitleRow.getCell(1).alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    
        const instructionRow = roleSheet.addRow([
          "All employees are set as User by default. Only update the Role column to POISUM Admin or Mission Creator where applicable in 'Employee Database' sheet.",
        ]);
        const instructionRowIndex = instructionRow.number;
        roleSheet.mergeCells(instructionRowIndex, 1, instructionRowIndex, 4);
        const instructionCell = roleSheet.getCell(instructionRowIndex, 1);
        instructionCell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
    
        roleSheet.addRow([]);
    
        // ===== HEADER =====
        const headersRole = ["No.", "Role", "Description"];
        const headerRowRole = roleSheet.addRow(headersRole);
    
        headerRowRole.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF39493F" }, // dark green/grey background
          };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // white text
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
    
        // ===== DATA ROWS =====
        const roleData = [
          [
            "1",
            "POISUM Admin",
            "A designated person with access to the POISUM Portal (backend), which serves as the company’s database and performance dashboard. Typically assigned to HR personnel and top management (decision-makers), this role oversees system setup, monitors overall performance, and ensures data accuracy.\nPOISUM Administrators automatically have Mission Creator privileges within the POISUM app.",
          ],
          [
            "2",
            "Mission Creator",
            "An appointed individual with the authority to create and manage missions, events, and campaigns within the POISUM app. They can track participation and access performance analytics for the activities they initiate, ensuring alignment with company objectives.",
          ],
          [
            "3",
            "User",
            "All company employees who have access to download and use the POISUM app. Users can participate in missions, events, and campaigns, accumulate points, and redeem rewards as defined by the company’s POISUM setup.",
          ],
        ];
    
        roleData.forEach((row) => {
          const dataRow = roleSheet.addRow(row);
          dataRow.eachCell((cell, colNumber) => {
            cell.alignment = {
              vertical: "top",
              horizontal: colNumber === 3 ? "left" : "center",
              wrapText: true,
            };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });
    
        // ===== COLUMN WIDTHS =====
        roleSheet.getColumn(1).width = 10; // Fixed width for "No."
        roleSheet.getColumn(2).width = 25; // Role name
        roleSheet.getColumn(3).width = 100; // Description (wide enough for long text)
    
        // ===== GENERAL ALIGNMENT CLEANUP =====
        roleSheet.columns.forEach((col) => {
          col.eachCell({ includeEmpty: true }, (cell) => {
            if (!cell.alignment) {
              cell.alignment = { vertical: "top", horizontal: "left", wrapText: true };
            }
          });
        });
    
        // set all font to Inter size 11
        workbook.eachSheet((sheet) => {
          sheet.eachRow((row) => {
            row.eachCell((cell) => {
              // preserve existing font styles (bold, italic, color, etc.)
              cell.font = { ...cell.font, name: "Inter", size: 11 };
            });
          });
        });
    
        // Export Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, "Poisum_Employee_Database.xlsx");
      };

  return (
    // <Button variant="contained" width={'50%'}  sx={{fontSize: 12, m: 0}} >
    <Button
      variant="contained"
      component="label"
      startIcon={<Download />}
      sx={{
        px: 4,
        py: 1.5,
        textTransform: "none",
        fontSize: 14,
        backgroundColor: color,
        "&:hover": {
          backgroundColor: color,
        },
      }}
      onClick={handleExport}
    >
      Download Template

    </Button>
  );
};

export default ExportEmployeeExcel;
